import { useCallback, useEffect, useRef, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../lib/firebase';

const LEGACY_STORAGE_KEY_PREFIX = 'valutten_hidden_brokers_';

interface UseBrokerVisibilityPreferencesReturn {
  hiddenBrokerIds: Set<string>;
  setHidden: (brokerId: string, hide: boolean) => void;
  loading: boolean;
  error: string | null;
}

/**
 * Company-wide hidden-broker preference for the Commission Settings page.
 * Source of truth: `companies/{companyId}/commission_settings/hidden_brokers`
 * (one shared doc per tenant — any admin updates it, all admins see it).
 *
 * Optimistic local update; server snapshot reconciles on write completion.
 * On first load after the localStorage → Firestore migration, if the
 * shared doc is missing AND a legacy `valutten_hidden_brokers_{companyId}`
 * localStorage value exists, seed the shared doc with it then clear the
 * legacy key.
 */
export function useBrokerVisibilityPreferences(
  companyId: string | undefined,
): UseBrokerVisibilityPreferencesReturn {
  const [hiddenBrokerIds, setHiddenBrokerIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const migrationAttemptedRef = useRef<string | null>(null);

  const writeToServer = useCallback(
    async (cid: string, nextSet: Set<string>) => {
      try {
        const fn = httpsCallable(functions, 'updateBrokerVisibility');
        await fn({ companyId: cid, hiddenBrokerIds: [...nextSet] });
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to save visibility';
        setError(msg);
        console.error('[useBrokerVisibilityPreferences] write failed:', err);
      }
    },
    [],
  );

  useEffect(() => {
    if (!companyId) {
      setHiddenBrokerIds(new Set());
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const prefRef = doc(db, `companies/${companyId}/commission_settings/hidden_brokers`);
    const unsubscribe = onSnapshot(
      prefRef,
      (snap) => {
        const serverList: string[] = snap.exists()
          ? (snap.data()?.hiddenBrokerIds as string[] | undefined) ?? []
          : [];

        if (!snap.exists() && migrationAttemptedRef.current !== companyId) {
          migrationAttemptedRef.current = companyId;
          try {
            const legacyRaw = localStorage.getItem(
              `${LEGACY_STORAGE_KEY_PREFIX}${companyId}`,
            );
            if (legacyRaw) {
              const legacyList = JSON.parse(legacyRaw);
              if (Array.isArray(legacyList) && legacyList.length > 0) {
                const legacySet = new Set<string>(
                  legacyList.filter((v) => typeof v === 'string'),
                );
                setHiddenBrokerIds(legacySet);
                void writeToServer(companyId, legacySet).then(() => {
                  try {
                    localStorage.removeItem(
                      `${LEGACY_STORAGE_KEY_PREFIX}${companyId}`,
                    );
                  } catch {
                    /* ignore */
                  }
                });
                setLoading(false);
                return;
              }
            }
          } catch {
            /* ignore malformed JSON */
          }
        }

        setHiddenBrokerIds(new Set(serverList));
        setLoading(false);
      },
      (err) => {
        console.error('[useBrokerVisibilityPreferences] snapshot error:', err);
        setError(err instanceof Error ? err.message : 'Failed to load preferences');
        setLoading(false);
      },
    );

    return () => unsubscribe();
  }, [companyId, writeToServer]);

  const setHidden = useCallback(
    (brokerId: string, hide: boolean) => {
      if (!companyId) return;
      setHiddenBrokerIds((prev) => {
        const next = new Set(prev);
        if (hide) next.add(brokerId);
        else next.delete(brokerId);
        void writeToServer(companyId, next);
        return next;
      });
    },
    [companyId, writeToServer],
  );

  return { hiddenBrokerIds, setHidden, loading, error };
}
